import React from 'react'
import styled from 'styled-components'
import DatePicker from 'react-datepicker'
import { CalendarOutlineIcon } from '../../../../../generic/icons/inputIcons'

interface BaseProps {
  className?: string
  leadingIcon?: React.ReactNode | null
}

interface InteractiveProps extends BaseProps {
  readOnly?: false
  selected: Date | number | null
  onChange: (d: Date | null) => void
  disabled?: boolean
  dateFormat?: string
}

interface ReadOnlyProps extends BaseProps {
  readOnly: true
  value: string
}

export type DateFieldProps = InteractiveProps | ReadOnlyProps

const DateField: React.FC<DateFieldProps> = (props) => {
  const renderedLeading = props.leadingIcon === null ? null : props.leadingIcon ?? <CalendarOutlineIcon />

  if (props.readOnly) {
    return (
      <Wrapper className={props.className} $hasLeading={!!renderedLeading} $readOnly>
        {renderedLeading && <IconSlot aria-hidden>{renderedLeading}</IconSlot>}
        <ReadonlyText>{props.value || '—'}</ReadonlyText>
      </Wrapper>
    )
  }

  const { selected, onChange, disabled, dateFormat = 'dd/MM/yy', className } = props

  return (
    <Wrapper className={className} $hasLeading={!!renderedLeading} $disabled={disabled}>
      {renderedLeading && <IconSlot aria-hidden>{renderedLeading}</IconSlot>}
      <PickerSlot>
        <DatePicker
          className='picker-input'
          disabled={disabled}
          dateFormat={dateFormat}
          selected={selected as Date}
          onChange={onChange}
          popperProps={{ strategy: 'fixed' }}
          popperPlacement='bottom-end'
        />
      </PickerSlot>
    </Wrapper>
  )
}

export default DateField

const Wrapper = styled.div<{ $hasLeading: boolean; $disabled?: boolean; $readOnly?: boolean }>`
  position: relative;
  width: 100%;
  min-height: 2.875rem;
  display: grid;
  grid-template-columns: ${({ $hasLeading }) => ($hasLeading ? 'auto 1fr' : '1fr')};
  align-items: center;
  gap: 0.625rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 0.875rem;
  padding: 0 0.75rem;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${({ theme, $readOnly, $disabled }) => ($readOnly || $disabled ? undefined : theme.colors.inputBorderHover)};
  }

  &:focus-within {
    border-color: ${({ theme, $readOnly, $disabled }) => ($readOnly || $disabled ? undefined : theme.colors.ink)};
    box-shadow: ${({ theme, $readOnly, $disabled }) => ($readOnly || $disabled ? 'none' : theme.shadows.focus)};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 2.5rem;
    padding: 0 0.625rem;
    gap: 0.5rem;
  }

  & .react-datepicker-popper {
    z-index: 20;
  }
`

const IconSlot = styled.span`
  color: ${({ theme }) => theme.colors.icon};
  display: grid;
  place-items: center;
  width: 1.25rem;
  height: 1.25rem;

  svg {
    width: 1.05rem;
    height: 1.05rem;
    flex-shrink: 0;
  }
`

const ReadonlyText = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  padding: 0.375rem 0;
  direction: rtl;
  text-align: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`

const PickerSlot = styled.div`
  width: 100%;

  .picker-input {
    border: 0;
    outline: 0;
    background: transparent;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    color: ${({ theme }) => theme.colors.ink};
    width: 100%;
    padding: 0.375rem 0;
    direction: rtl;
    text-align: start;
  }

  .picker-input:disabled {
    color: ${({ theme }) => theme.colors.ink2};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    .picker-input {
      font-size: ${({ theme }) => theme.textMobile.lg};
    }
  }
`
